import React, { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { matriculaService } from 'services/matriculaService';
import { catalogoService } from 'services/usuarioService';
import { CursosEstudianteResponse } from 'types/matricula.types';
import { AñoEscolar } from 'types/usuario.types';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// MisCursosEstudiantePage – Cursos matriculados del estudiante
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const CARD_ACCENTS = [
  { border: 'border-t-blue-500',   icon: 'bg-blue-100 text-blue-600' },
  { border: 'border-t-purple-500', icon: 'bg-purple-100 text-purple-600' },
  { border: 'border-t-green-500',  icon: 'bg-green-100 text-green-600' },
  { border: 'border-t-amber-500',  icon: 'bg-amber-100 text-amber-600' },
  { border: 'border-t-pink-500',   icon: 'bg-pink-100 text-pink-600' },
  { border: 'border-t-indigo-500', icon: 'bg-indigo-100 text-indigo-600' },
  { border: 'border-t-teal-500',   icon: 'bg-teal-100 text-teal-600' },
  { border: 'border-t-orange-500', icon: 'bg-orange-100 text-orange-600' },
];

const BookIcon = ({ cls }: { cls: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className={cls}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
  </svg>
);

const MisCursosEstudiantePage: React.FC = () => {
  const { user } = useAuth();

  const [data, setData]                         = useState<CursosEstudianteResponse | null>(null);
  const [añosEscolares, setAñosEscolares]       = useState<AñoEscolar[]>([]);
  const [anioSeleccionado, setAnioSeleccionado] = useState<number | null>(null);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState<string | null>(null);

  // Cargar catálogo de años escolares
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const anios = await catalogoService.getAñosEscolares();
        if (cancelled) return;
        setAñosEscolares(anios);
        if (anios.length > 0) setAnioSeleccionado(anios[0].idAnioEscolar);
      } catch {
        if (!cancelled) {
          setError('No se pudieron cargar los años escolares');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cargar cursos del estudiante cuando cambia el año
  useEffect(() => {
    if (!user || !anioSeleccionado) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await matriculaService.getCursosByEstudiante(user.idUsuario, anioSeleccionado);
        if (!cancelled) setData(resp);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message ?? 'Error al cargar los cursos');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, anioSeleccionado]);

  const cursos      = data?.cursos ?? [];
  const anioLabel   = añosEscolares.find(a => a.idAnioEscolar === anioSeleccionado)?.anio;
  const anioActual  = new Date().getFullYear();
  const esAnioActual = anioLabel === anioActual;

  return (
    <div className="space-y-6">

      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white shadow-lg">
        <p className="text-blue-200 text-sm font-medium mb-1">Estudiante</p>
        <h2 className="text-2xl font-bold">
          {data ? `${data.estudianteNombres} ${data.estudianteApellidos}` : `${user?.nombres} ${user?.apellidos}`}
        </h2>
        {data && (
          <p className="text-blue-200 text-sm mt-1">
            {data.gradoNombre} — Sección {data.seccionNombre} &bull; Año {data.anio}
          </p>
        )}
      </div>

      {/* Selector de año + resumen */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 flex-1">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342" />
              </svg>
            </div>
            <p className="text-2xl font-bold text-slate-800">{cursos.length}</p>
            <p className="text-slate-400 text-sm">Cursos matriculados</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
              </svg>
            </div>
            <p className="text-2xl font-bold text-slate-800">
              {new Set(cursos.map(c => c.docenteId)).size}
            </p>
            <p className="text-slate-400 text-sm">Docentes</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
              </svg>
            </div>
            <p className="text-2xl font-bold text-slate-800">{anioLabel ?? '—'}</p>
            <p className="text-slate-400 text-sm">Año escolar</p>
          </div>
        </div>

        {/* Selector de año */}
        <div className="flex items-center gap-2 self-start">
          <label htmlFor="anio-cursos" className="text-sm font-medium text-slate-600">Año:</label>
          <select
            id="anio-cursos"
            value={anioSeleccionado ?? ''}
            onChange={e => setAnioSeleccionado(Number(e.target.value))}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            {añosEscolares.map(a => (
              <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <SpinnerIcon />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      ) : cursos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
            className="w-12 h-12 text-slate-200 mx-auto mb-3">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342" />
          </svg>
          <p className="text-slate-400 text-sm font-medium">No se encontraron cursos</p>
          <p className="text-slate-300 text-xs mt-1">No tienes cursos asignados para este año escolar.</p>
        </div>
      ) : (
        <>
          {/* Sección de módulos */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-lg font-bold text-slate-800">Tus módulos de aprendizaje</h3>
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs font-semibold text-slate-400">{cursos.length} módulos</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cursos.map((c, idx) => {
                const accent = CARD_ACCENTS[idx % CARD_ACCENTS.length];
                return (
                  <div
                    key={c.asignacionAcademicaId}
                    className={`bg-white rounded-2xl border border-slate-100 shadow-sm
                      border-t-4 ${accent.border} flex flex-col
                      transition-all hover:shadow-md hover:-translate-y-0.5`}
                  >
                    <div className="p-5 flex-1">
                      {/* Icono + estado */}
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent.icon}`}>
                          <BookIcon cls="w-5 h-5" />
                        </div>
                        {esAnioActual ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold
                            px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            En progreso
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold
                            px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            Completado
                          </span>
                        )}
                      </div>

                      {/* Nombre del curso */}
                      <h3 className="font-bold text-slate-800 text-base leading-snug mb-3">
                        {c.cursoNombre}
                      </h3>

                      {/* Docente */}
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400 flex-shrink-0">
                          <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
                        </svg>
                        <span className="truncate">{c.docenteNombres} {c.docenteApellidos}</span>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="px-5 py-3 border-t border-slate-100">
                      <button className="w-full flex items-center justify-center gap-2 text-sm font-semibold
                        text-blue-700 hover:text-blue-900 transition-colors group">
                        Ver detalles del curso
                        <svg viewBox="0 0 20 20" fill="currentColor"
                          className="w-4 h-4 transition-transform group-hover:translate-x-0.5">
                          <path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MisCursosEstudiantePage;
